import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MessengerLayout from "../../layout/MessengerLayout";
import { Briefcase, BarChart2, Calendar, BookOpen, ArrowLeft } from "lucide-react";

const API_BASE = (process.env.REACT_APP_API_BASE || "http://localhost:8000") + "/api";

const card =
  "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

// Simple key-value row
function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-white/5 last:border-0">
      <span className="text-xs text-[#00ff90]/50 uppercase tracking-wider shrink-0">{label}</span>
      <span className="text-sm text-white text-right">{value ?? <span className="text-white/20 italic">Not set</span>}</span>
    </div>
  );
}

// Section wrapper
function Section({ title, icon, children }) {
  return (
    <div className={card}>
      <div className="flex items-center gap-2 px-5 py-4 border-b border-white/5">
        <span className="text-[#00ff90]/60">{icon}</span>
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">{title}</span>
      </div>
      <div className="px-5 py-4 space-y-0">{children}</div>
    </div>
  );
}

export default function EmployeeWorkProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [work, setWork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("token")) navigate("/login");
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!id || !token) return;
    setLoading(true);
    fetch(`${API_BASE}/manager/employee/${id}/work`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => { if (!res.ok) throw new Error(); return res.json(); })
      .then((data) => setWork(data))
      .catch(() => { setError(true); setWork(null); })
      .finally(() => setLoading(false));
  }, [id]);

  const tenure = useMemo(() => {
    if (!work?.start_date) return null;
    const start = new Date(work.start_date);
    const now = new Date();
    const totalMonths =
      (now.getFullYear() - start.getFullYear()) * 12 +
      (now.getMonth() - start.getMonth()) -
      (now.getDate() < start.getDate() ? 1 : 0);
    return `${Math.floor(totalMonths / 12)}y ${totalMonths % 12}m`;
  }, [work?.start_date]);

  return (
    <MessengerLayout>
      <div className="p-6 text-white space-y-5">

        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-xs text-white/40 hover:text-[#00ff90] transition-colors"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <div>
            <h1 className="text-xl font-bold text-[#00ff90]">
              {loading ? "Loading…" : work?.name || "Work Profile"}
            </h1>
            <p className="text-white/30 text-xs mt-0.5 flex items-center gap-1">
              Work information only
              <span className="px-1.5 py-0.5 rounded-full border border-white/10 text-white/20">
                Personal details hidden
              </span>
            </p>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {[...Array(3)].map((_, i) => (
              <div key={i} className={`${card} h-48 animate-pulse`} />
            ))}
          </div>
        )}

        {/* Error */}
        {!loading && (error || !work) && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-6 text-red-300 text-sm">
            Unable to load employee work profile.
          </div>
        )}

        {/* Content — manager-visible fields only */}
        {!loading && !error && work && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

            {/* Role & Organisation — visible to managers */}
            <Section title="Role & Organisation" icon={<Briefcase size={14} />}>
              <Row label="Name"            value={work.name} />
              <Row label="Role"            value={work.role} />
              <Row label="Department"      value={work.department} />
              <Row label="Manager"         value={work.manager_name} />
              <Row label="Employment Type" value={work.employment_type} />
              <Row label="Work Location"   value={work.work_location} />
              <Row label="Start Date"      value={work.start_date} />
              <Row label="Tenure"          value={tenure} />
            </Section>

            {/* Projects & Skills — visible to managers */}
            <Section title="Projects & Skills" icon={<BookOpen size={14} />}>
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-2">Current Projects</p>
                  {work.projects?.length ? (
                    <ul className="space-y-1">
                      {work.projects.map((p, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-white/70">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#00ff90]/40 shrink-0" />{p}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-white/20 text-sm italic">None assigned</p>
                  )}
                </div>

                <div>
                  <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-2">Skills</p>
                  {work.skills?.length ? (
                    <div className="flex flex-wrap gap-2">
                      {work.skills.map((s, i) => (
                        <span key={i}
                          className="text-xs px-2.5 py-1 rounded-full border border-[#00ff90]/20 bg-[#00ff90]/5 text-white/60">
                          {s}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-white/20 text-sm italic">None listed</p>
                  )}
                </div>
              </div>
            </Section>

            {/* Performance & Attendance — visible to managers */}
            <Section title="Performance & Attendance" icon={<BarChart2 size={14} />}>
              <Row label="Performance Rating" value={work.performance_rating} />
              <Row label="Last Appraisal"     value={work.last_appraisal_date} />
              <Row label="Present (30d)"      value={work.attendance?.present_days} />
              <Row label="Late (30d)"         value={work.attendance?.late_days} />
              <Row label="Absent (30d)"       value={work.attendance?.absent_days} />

              <div className="mt-4 pt-3 border-t border-white/5">
                <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">Leave Balance</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: "Annual", value: work.leave_balance?.annual },
                    { label: "Sick",   value: work.leave_balance?.sick },
                  ].map(({ label, value }) => (
                    <div key={label} className="rounded-lg border border-[#00ff90]/15 bg-[#00ff90]/5 p-3">
                      <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider">{label}</p>
                      <p className="text-lg font-bold text-white mt-1">{value ?? "—"}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Section>

          </div>
        )}

        {/* Hidden fields notice */}
        {!loading && !error && work && (
          <div className="flex items-center gap-2 text-xs text-white/20 border border-white/5 rounded-lg px-4 py-2.5 bg-white/2">
            <span></span>
            
          </div>
        )}

      </div>
    </MessengerLayout>
  );
}